//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { ColumnModel } from "../api/api.models"

import { CreateEditInitParams } from '../common/create-edit-popup/create-edit-popup.controller'
import { CommonHelper, ConvertHelper } from "../../helpers"
import { DataProvider } from "../../helpers/data-provider"
import { LocalizationHelper } from "../../helpers/localization-helper"
import { ToastHelper } from '../../helpers/toast-helper'

import { Enums } from "../../models/common/enums"
import { OpIssue } from '../../models/core/op-issue'
import { OpTask } from '../../models/core/op-task'
import { SESSION } from "../../models/custom/session"

export class IssueHelper {

    // Consts

    public static get FILE_NAME_PREFIX(): string { return 'issues' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType  { return Enums.ReferenceType.IdIssue }  
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName  { return Enums.SimaxModuleName.Issue }
    public static get ISSUES_OPEN_CREATE_ISSUE_TASK(): string { return 'ISSUES_OPEN_CREATE_ISSUE_TASK' }

    private static get LOG_STRING(): string { return '[SIMAX.Issues.IssuesHelper] - %s' }

    // Consts End


    // Methods

    public static deleteIssue(dataProvider: DataProvider, referenceObject: OpIssue, moduleName: Enums.SimaxModuleName): JQueryPromise<any> {

        let toDelete: OpIssue = new OpIssue(null)

        toDelete.idIssue = referenceObject.idIssue
        toDelete.creationDate = referenceObject.creationDate

        return dataProvider.Issue
            .deleteIssue(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, moduleName))
            })
            .fail((error: any) => {
                if (SESSION.isType(error)) {
                    const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                    ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))

                    console.warn(IssueHelper.LOG_STRING, `Unable to delete issue. Error: ${errorMsg}.`)
                } else {
                    ToastHelper.showError(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, moduleName))
                }
            })
    }

    public static saveIssue(dataProvider: DataProvider, issue: OpIssue, moduleName: Enums.SimaxModuleName, isBulkSave: boolean = false): JQueryPromise<OpIssue> {
        const deferred: JQueryDeferred<OpIssue> = $.Deferred<OpIssue>()
        dataProvider.Issue
            .saveIssue(issue)
            .then((savedObject: OpIssue) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, moduleName))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.issueSaved, true, 0)
                }

                deferred.resolve(savedObject)
            })
            .fail((error: any) => {

                if (!isBulkSave) {
                    if (SESSION.isType(error)) {
                        const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                        ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                        console.warn(IssueHelper.LOG_STRING, `Unable to save issue. Error: ${errorMsg}.`)
                    } else {
                        ToastHelper.showError(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, moduleName))
                    }
                }

                deferred.reject(error)
            })

        return deferred.promise()
    }

    public static saveIssueTasks(dataProvider: DataProvider, moduleName: Enums.SimaxModuleName, issue: OpIssue, added: OpTask[], removed: OpTask[]): JQueryPromise<OpIssue> {
        const newTasks: OpTask[] = added.map((newTaskData: any) => {
            const task: OpTask = new OpTask(null)

            task.idTask = newTaskData.id
            task.opState = Enums.OpChangeState.New

            return task
        })

        const removedTasks: OpTask[] = removed.map((removedTaskData: any) => {
            const task: OpTask = new OpTask(null)

            task.idTask = removedTaskData.id
            task.opState = Enums.OpChangeState.Delete

            return task
        })

        const removedTaskIds: number[] = removedTasks.map((removedTasks: OpTask) => removedTasks.idTask)

        const loadedTasks: OpTask[] = issue.taskList
            .filter((loadedTasks: OpTask) => !_.contains(removedTaskIds, loadedTasks.idTask))
            .map((loadedTasks: OpTask) => {
                const task = new OpTask(null)

                task.idTask = loadedTasks.idTask
                task.opState = Enums.OpChangeState.Loaded

                return task
            })

        const objectToSave: OpIssue = new OpIssue(null)

        objectToSave.idIssue = issue.idIssue
        objectToSave.opState = Enums.OpChangeState.Modified
        objectToSave.taskList = [].concat(...loadedTasks, ...newTasks, ...removedTasks);
        objectToSave.creationDate = issue.creationDate
        objectToSave.realizationDate = issue.realizationDate

        return IssueHelper.saveIssue(dataProvider, objectToSave, moduleName);
    }
    
    public static getCreateEditOptions(referenceObjectId: number = 0, referenceObject: OpIssue = null): CreateEditInitParams {
        return {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: IssueHelper.REFERENCE_TYPE,
            moduleName: IssueHelper.SIMAX_MODULE_NAME,
            referenceObject: referenceObject,
            referenceObjectId: referenceObjectId,
            sections: null,
            useBusinessObjects: true,
        }
    }

    public static getIssueAttachmentGridColumns(): ColumnModel[] {

        const physicalFileName: ColumnModel = new ColumnModel()
        const size: ColumnModel = new ColumnModel()
        const description: ColumnModel = new ColumnModel()
        const insertDate: ColumnModel = new ColumnModel()
        const downloadButton: ColumnModel = new ColumnModel()

        physicalFileName.idName = 'physicalFileName'
        physicalFileName.name = 'PhysicalFileName'
        physicalFileName.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name)
            
        size.idName = 'size'
        size.name = 'Size'
        size.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Size) + ' [Mb]'
        size.type = Enums.DataTypeClass.Decimal
    
        description.idName = 'description'
        description.name = 'Description'
        description.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description) 
    
        insertDate.idName = 'insertDate'
        insertDate.name = 'InsertDate'
        insertDate.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.FillingDate)
        insertDate.type = Enums.DataTypeClass.Datetime

        return [physicalFileName, size, description, insertDate]
    }

    public static getIssueEquipmentGridColumns(): ColumnModel[] {

        const device: ColumnModel = new ColumnModel()
        const meter: ColumnModel = new ColumnModel()
        const startTime: ColumnModel = new ColumnModel()

        device.idName = 'device'
        device.name = 'Device'
        device.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Device)
        device.type = Enums.DataTypeClass.Decimal

        meter.idName = 'meter'
        meter.name = 'Meter'
        meter.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Meter)
        meter.type = Enums.DataTypeClass.Text
    
        startTime.idName = 'startTime'
        startTime.name = 'StartTime'
        startTime.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.StartTime)
        startTime.type = Enums.DataTypeClass.Datetime

        return [device, meter, startTime]
    }
} 
